/**
 * Contract Status — Phase 6a.10 (introduced), 6a.11 (server-rejection),
 * 6a.12/F-2 (out-of-period / cut-off), 6a.12/F-3 (dedicated period-violation
 * tier) per Spec §21.7.3 v1.13.
 *
 * Derives a contract's display status from its data, the form dirty flag,
 * any backend rejection state, and the engagement's audit period for
 * cut-off enforcement.
 *
 * Status priority order (Spec §21.7.3 v1.13):
 *   1. server-rejected   — backend rejected at last Calculate (technical, definitive)
 *   2. period-violation  — conceptual cut-off error (contract entirely outside
 *                          engagement audit period). Ranks ABOVE partial because
 *                          fixing Zod errors does not resolve a date mismatch.
 *   3. empty             — no data or never dirtied
 *   4. partial           — Zod safeParse fails (auditor's WIP, fill problem)
 *   5. filled            — all checks pass
 *
 * Schema-unchanged principle (Spec §21.7.3.1 Decision #6): the period
 * check lives HERE, not in `contractFormSchema`. The schema validates a
 * contract in isolation; engagement context (audit period bounds) is
 * orthogonal and naturally available at the status-derivation layer.
 * This keeps the schema usable as the canonical input contract for both
 * the frontend AND the backend.
 */

import { addMonths, parseISO } from "date-fns";

import i18n from "@/i18n";
import type { ContractInput } from "@/types/contract";

import {
  buildContractFormSchema,
  contractFormSchema,
} from "../contract-form/schemas/contractFormSchema";

export type ContractStatus =
  | "empty"
  | "partial"
  | "filled"
  | "server-rejected"
  | "period-violation";

export interface ContractStatusContext {
  contract: ContractInput | null;
  isDirty: boolean;
  /**
   * Backend Zod error messages for this contract from the most recent
   * Calculate attempt. When non-empty, status is forced to
   * `server-rejected`.
   */
  serverRejection?: string[];
  /**
   * Engagement audit-period bounds (Phase 6a.12/F-2, Spec §21.7.3.1).
   * When provided, an in-isolation Zod-valid contract that lies entirely
   * outside `[start, end]` is downgraded to `partial`. Optional for
   * backwards compatibility — callers in legacy code paths (no
   * engagement context) keep their existing behaviour.
   */
  auditPeriod?: {
    start: string; // ISO date YYYY-MM-DD
    end: string;   // ISO date YYYY-MM-DD
  };
  /**
   * Phase 6a.23 — engagement functional currency. Threaded through so
   * the FX-conditional schema can judge whether the contract's
   * `currency` makes it an FX contract. Defaults to `"TRY"` for legacy
   * callers — matches the form's default and the pre-Phase-6a.23
   * behaviour where the static schema treated TRY-functional as the
   * implicit assumption.
   */
  functionalCurrency?: string;
}

/**
 * Internal cut-off check. Returns `true` when the contract's
 * `[commencementDate, commencementDate + leaseTermMonths]` interval
 * does NOT overlap the audit period at any point.
 *
 * Boundary semantics (Spec §21.7.3.1 Critical Rule #5):
 *   • Strict `<` / `>` — a contract that ends ON the period start
 *     (`ce === ps`) or starts ON the period end (`cs === pe`) IS in
 *     period. The boundary day itself is included.
 */
function isOutOfAuditPeriod(
  contract: ContractInput,
  period: { start: string; end: string },
): boolean {
  try {
    const cs = parseISO(contract.commencementDate);
    const ce = addMonths(cs, contract.leaseTermMonths);
    const ps = parseISO(period.start);
    const pe = parseISO(period.end);
    if (
      Number.isNaN(cs.getTime()) ||
      Number.isNaN(ce.getTime()) ||
      Number.isNaN(ps.getTime()) ||
      Number.isNaN(pe.getTime())
    ) {
      // Invalid date string — defer to Zod which has already had its
      // turn; treat as in-period to avoid blocking on transient input.
      return false;
    }
    // Scenario A: contract ends strictly before audit period starts.
    if (ce < ps) return true;
    // Scenario B: contract starts strictly after audit period ends.
    if (cs > pe) return true;
    // Scenarios C, D: overlapping or wholly-within → in scope.
    return false;
  } catch {
    return false;
  }
}

export function deriveContractStatus(
  ctx: ContractStatusContext,
): ContractStatus {
  // 1. server-rejected — backend definitively rejected; technical certainty.
  if (ctx.serverRejection && ctx.serverRejection.length > 0) {
    return "server-rejected";
  }
  // 2. period-violation (Phase 6a.12/F-3) — conceptual cut-off error.
  //    Ranks ABOVE partial: if dates are wrong, fixing Zod errors won't help.
  //    Requires both a contract object AND audit period context — empty
  //    slots never trigger period checks.
  if (
    ctx.contract &&
    ctx.auditPeriod &&
    isOutOfAuditPeriod(ctx.contract, ctx.auditPeriod)
  ) {
    return "period-violation";
  }
  // 3. empty — no data or never dirtied.
  if (!ctx.contract || !ctx.isDirty) return "empty";
  // 4. partial — Zod fails; auditor is still filling.
  // Phase 6a.23 — schema built with the engagement's functional
  // currency so FX-conditional rules judge correctly.
  const schema = ctx.functionalCurrency
    ? buildContractFormSchema({
        functionalCurrency: ctx.functionalCurrency,
      })
    : contractFormSchema;
  const zodResult = schema.safeParse(ctx.contract);
  if (!zodResult.success) return "partial";
  // 5. filled — all checks pass.
  return "filled";
}

/**
 * MINI_FIX_CC — Maps a dotted Zod issue path (e.g. `payment.amount`,
 * `priorPeriodBalances.rouGrossOpening`, `modifications.0.newDiscountRate`,
 * `historicalRates.0.date`) to a COMPLETE i18n key whose target label
 * already exists in BOTH en.json and tr.json (REVIEW_CC audit confirmed
 * every leaf). The caller in `getContractValidationErrors` passes the
 * returned key straight to `i18n.t(...)` — no `form.` prefix is added at
 * the call site (each return value carries its own namespace).
 *
 * Resolution order (each step is exhaustive over a class of leaves; if
 * none match, the previous behaviour of leaking the raw path is replaced
 * by step 5's `form.<leaf>` flat fallback, which resolves every audited
 * top-level leaf without any new locale keys):
 *
 *   1. Exact-match map — the 6 historical entries, now expressed as full
 *      keys (no behaviour change for those 6).
 *   2. Strip numeric index segments so array-leaf paths
 *      (`modifications.0.newDiscountRate`, `historicalRates.0.date`)
 *      reduce to their structural shape (`modifications.newDiscountRate`,
 *      `historicalRates.date`) and share the lookup with their
 *      object-nested equivalents.
 *   3. Group-prefix map for the 14 leaves whose labels live OUTSIDE
 *      `form.*` (priorPeriodBalances ×9, termination.assetReturnDate,
 *      FX rate-row date/rate ×4). Both `historicalRates.date|rate` and
 *      `paymentDateRates.date|rate` intentionally resolve to the SAME
 *      `engagement.contract.fx.historicalRates.headers.*` keys — there
 *      is no separate `paymentRates.headers` block in either locale and
 *      "Tarih"/"Kur" is semantically correct for both tables.
 *   4. Object-leaf guard — `form.description` and `form.assetClass` are
 *      i18n OBJECTS (`{label, placeholder, ...}`), not strings; calling
 *      `t()` on them surfaces "[object Object]" / "returned an object
 *      instead of string" (BB6-1). The exact map (step 1) already pins
 *      the TOP-LEVEL `description`/`assetClass` entries, but
 *      `termination.description` / `modifications.0.description` reach
 *      the fallback — pin those too here, defensively.
 *   5. Flat last-segment fallback — `form.<leaf>`. Resolves every
 *      remaining audited leaf (terminationDate, newDiscountRate,
 *      resetDate, indexType, commencementSpotRate, priorReportingDate,
 *      priorReportingTolerance, notes, modificationId, …) because each
 *      one already exists as a string entry in BOTH locales.
 */
function pathToFormKey(path: ReadonlyArray<string | number>): string {
  const segments = path.map(String);
  const joined = segments.join(".");

  // 1. Exact-match map — preserves the 6 historical entries verbatim.
  const exact: Record<string, string> = {
    "payment.amount": "form.paymentAmount",
    "payment.frequency": "form.paymentFrequency",
    "payment.timing": "form.paymentTiming",
    "payment.firstPaymentDate": "form.firstPaymentDate",
    description: "form.description.label",
    assetClass: "form.assetClass.label",
  };
  if (joined in exact) return exact[joined]!;

  // 2. Drop numeric index segments → structural path.
  const seg = segments.filter((s) => !/^\d+$/.test(s));
  const structural = seg.join(".");

  // 3. Group-prefix map. Target keys verified to exist in BOTH en.json
  //    and tr.json by the REVIEW_CC audit; do NOT add new entries here
  //    without re-running the audit.
  const group: Record<string, string> = {
    "priorPeriodBalances.rouGrossOpening":
      "engagement.contract.priorReconciliation.fields.rouGrossOpening.label",
    "priorPeriodBalances.rouAccumulatedDepreciation":
      "engagement.contract.priorReconciliation.fields.rouAccumulatedDepreciation.label",
    "priorPeriodBalances.leaseLiabilityShortTerm":
      "engagement.contract.priorReconciliation.fields.leaseLiabilityShortTerm.label",
    "priorPeriodBalances.leaseLiabilityLongTerm":
      "engagement.contract.priorReconciliation.fields.leaseLiabilityLongTerm.label",
    "priorPeriodBalances.restoration":
      "engagement.contract.priorReconciliation.fields.restoration.label",
    "priorPeriodBalances.prepaid":
      "engagement.contract.priorReconciliation.fields.prepaid.label",
    "priorPeriodBalances.incentiveLiability":
      "engagement.contract.priorReconciliation.fields.incentiveLiability.label",
    "priorPeriodBalances.liabilityContractCcy":
      "engagement.contract.priorReconciliation.fields.liabilityContractCcy.label",
    "priorPeriodBalances.priorYearClosingRate":
      "engagement.contract.priorReconciliation.fields.priorYearClosingRate.label",
    "termination.assetReturnDate":
      "engagement.contract.termination.fields.assetReturnDate.label",
    "historicalRates.date":
      "engagement.contract.fx.historicalRates.headers.date",
    "historicalRates.rate":
      "engagement.contract.fx.historicalRates.headers.rate",
    "paymentDateRates.date":
      "engagement.contract.fx.historicalRates.headers.date",
    "paymentDateRates.rate":
      "engagement.contract.fx.historicalRates.headers.rate",
  };
  if (structural in group) return group[structural]!;

  // 4. Object-leaf guard for nested description/assetClass paths.
  //    `form.<leaf>` here would be an object, not a string. Same trap
  //    BB6-1 pinned for the top-level cases above.
  const leaf = seg[seg.length - 1] ?? joined;
  if (leaf === "description" || leaf === "assetClass") {
    return `form.${leaf}.label`;
  }

  // 5. Flat last-segment fallback. Every remaining audited leaf has a
  //    `form.<leaf>` string entry in both locales.
  return `form.${leaf}`;
}

/**
 * Extracts a flat list of LOCALIZED Zod error messages from a contract
 * that fails frontend validation. Used by the `partial` tooltip body in
 * `ContractStatusDot` (slot strip + ContractListPage).
 *
 * Phase 6a.12/F-4 (Spec §11.11.1): each `issue.message` is an `error.*`
 * i18n key emitted by the schema; we resolve both the field label
 * (`form.<fieldKey>`) and the message via the shared i18n instance so the
 * tooltip body reads as audit-grade text (e.g. "İskonto Oranı (Yıllık %):
 * Pozitif bir sayı olmalıdır") and toggles language with the active locale.
 *
 * The helper is read on every tooltip render, so it intentionally does
 * NOT memoize — language switches must propagate immediately.
 */
export function getContractValidationErrors(
  contract: ContractInput | null,
  functionalCurrency?: string,
): string[] {
  if (!contract) return [];
  const schema = functionalCurrency
    ? buildContractFormSchema({ functionalCurrency })
    : contractFormSchema;
  const result = schema.safeParse(contract);
  if (result.success) return [];
  return result.error.issues.map((issue) => {
    // MINI_FIX_CC — `pathToFormKey` now returns a COMPLETE i18n key
    // (including its namespace), so the call site no longer concatenates
    // a `form.` prefix. The previous shape would have double-prefixed any
    // key that already starts with `form.` AND would have failed every
    // group-prefixed key under `engagement.contract.*`.
    const fieldKey = pathToFormKey(issue.path);
    const fieldLabel = i18n.t(fieldKey as never, {
      defaultValue: fieldKey || "(root)",
    }) as string;
    // Resolve i18n-keyed messages; legacy literal messages (none in the
    // current schema, but kept defensive for safety) pass through as-is.
    const looksLikeKey = /^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z]/.test(issue.message);
    const message = looksLikeKey
      ? (i18n.t(issue.message as never, {
          defaultValue: issue.message,
        }) as string)
      : issue.message;
    return `${fieldLabel}: ${message}`;
  });
}

/**
 * Returns the canonical i18n key for an out-of-period contract, or
 * `null` if the contract is in-period / has malformed dates / is null.
 *
 * Used by `ContractStatusDot` and the contract list to render a single
 * audit-friendly tooltip message. Caller resolves the key via `t()`.
 *
 * Phase 6a.12/F-2 (Spec §21.7.3.1). Two canonical messages:
 *   • `outOfPeriodEndsBefore`  — Scenario A: ended before period start
 *   • `outOfPeriodStartsAfter` — Scenario B: starts after period end
 */
export function getOutOfPeriodMessageKey(
  contract: ContractInput | null,
  period: { start: string; end: string },
):
  | "engagement.contract.status.outOfPeriodEndsBefore"
  | "engagement.contract.status.outOfPeriodStartsAfter"
  | null {
  if (!contract) return null;
  try {
    const cs = parseISO(contract.commencementDate);
    const ce = addMonths(cs, contract.leaseTermMonths);
    const ps = parseISO(period.start);
    const pe = parseISO(period.end);
    if (
      Number.isNaN(cs.getTime()) ||
      Number.isNaN(ce.getTime()) ||
      Number.isNaN(ps.getTime()) ||
      Number.isNaN(pe.getTime())
    ) {
      return null;
    }
    if (ce < ps) return "engagement.contract.status.outOfPeriodEndsBefore";
    if (cs > pe) return "engagement.contract.status.outOfPeriodStartsAfter";
    return null;
  } catch {
    return null;
  }
}
